import { toPercentage, toSignificant } from '@/utilities/formatting';
import DataTable from '@components/DataTable';
import { SWS_ANALYSIS_MODEL } from '@/constants';

interface ExcessReturnsModelProps {
  uniqueSymbol: string;
  reportingCurrencyISO: string;
  reportingCurrencySymbol: string;
  companyShortName: string;
  riskFreeRate: number;
  costOfEquity: number;
  pvtv: number;
  npv: number;
  excessReturns: {
    stableBookValue: number;
    returnOnEquityAvg: number;
    stableEPS: number;
    stableEPSSource: string;
    stableBookValueSource: string;
    bookValue: number;
    equityCost: number;
    excessReturn: number;
  };
}

const ExcessReturnsModel = ({
  uniqueSymbol,
  reportingCurrencyISO,
  reportingCurrencySymbol,
  companyShortName,
  riskFreeRate,
  costOfEquity,
  pvtv,
  npv,
  excessReturns,
}: ExcessReturnsModelProps) => (
  <div>
    <p>
      {`The calculations below outline how an intrinsic value for ${companyShortName} is arrived at using the Excess Return Model. This approach is used for finance firms where free cash flow is difficult to estimate.`}
    </p>
    <p>
      In the Excess Return Model the value of a firm can be written as the sum
      of capital invested currently in the firm and the present value of excess
      returns that the firm expects to make in the future.
    </p>
    <p>
      The model is sensitive to the Return on Equity of the company versus the
      Cost of Equity, how these are calculated is detailed below the main
      calculation.
    </p>
    <p>Note the calculations below are per share.</p>
    <p>
      <span>See our </span>
      <strong>
        <a
          href={`${SWS_ANALYSIS_MODEL}/MODEL.markdown#excess-returns-model--how-is-this-calculated`}
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          documentation
        </a>
      </strong>
      <span> to learn about this calculation.</span>
    </p>
    <DataTable light>
      <caption>{`${uniqueSymbol} Value of Excess Returns`}</caption>
      <thead>
        <tr>
          <th />
          <th>Calculation</th>
          <th>Result</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Excess Returns</td>
          <td>
            <span>{`= (Stable Return on equity – Cost of equity) x (Book Value of Equity per share)`}</span>
            <br />
            <span>
              {`= (${toPercentage(
                excessReturns.returnOnEquityAvg,
                1
              )} - ${toPercentage(
                costOfEquity,
                2
              )}) x ${reportingCurrencySymbol}${toSignificant(
                excessReturns.stableBookValue,
                2
              )}`}
            </span>
          </td>
          <td>
            {`${reportingCurrencySymbol}${toSignificant(
              excessReturns.excessReturn,
              2
            )}`}
          </td>
        </tr>
        <tr>
          <td>Terminal Value of Excess Returns</td>
          <td>
            <span>
              = Excess Returns / (Cost of Equity - Expected Growth Rate)
            </span>
            <br />
            <span>{`= ${reportingCurrencySymbol}${toSignificant(
              excessReturns.excessReturn,
              2
            )} / (${toPercentage(costOfEquity, 2)} - ${toPercentage(
              riskFreeRate,
              2
            )})`}</span>
          </td>
          <td>{`${reportingCurrencySymbol}${toSignificant(pvtv, 2)}`}</td>
        </tr>
        <tr>
          <td>Value of Equity</td>
          <td>
            <span>
              = Book Value per share + Terminal Value of Excess Returns
            </span>
            <br />
            <span>
              {`${reportingCurrencyISO}${toSignificant(
                excessReturns.stableBookValue,
                2
              )} + ${reportingCurrencySymbol}${toSignificant(pvtv, 2)}`}
            </span>
          </td>
          <td>{`${reportingCurrencySymbol}${toSignificant(npv, 2)}`}</td>
        </tr>
      </tbody>
    </DataTable>
  </div>
);

export default ExcessReturnsModel;
